import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import _ from "lodash";
import * as userActions from "actions/user";
import * as navigationActions from "actions/navigation";
import * as commonActions from "actions/common";
import routePaths from "app/components/Router/routePaths";
import constants from "constants";
import { getSsoStartUrl } from "apis/auth";
import {
  createSsoVerifier,
  rememberSsoVerifier,
  takeSsoVerifier,
} from "utils/ssoVerifier";
import Input from "widgets/Input";
import Button from "widgets/Button";
import PrimaryLogo from "shared/PrimaryLogo";
import GoogleIcon from "icons/GoogleIcon";
import styles from "./index.scss";

const SSO_CODE_PARAM = "sso_code";
const SSO_ERROR_PARAM = "sso_error";
const SSO_DEFAULT_ERROR = "Could not sign in with Google. Please try again.";
// Keys iamauth sends back as sso_error. Anything else gets the default message.
const SSO_ERROR_MESSAGES = {
  unavailable:
    "Sign in with Google is not available right now. Please log in with your password.",
  cancelled: "Google sign-in was cancelled.",
  not_allowed: "Please sign in with your upGrad Google account.",
  no_account:
    "There is no Phoenix account for this Google email. Please contact your admin.",
  account_disabled:
    "Your Phoenix account is deactivated. Please contact your admin.",
  expired: "The sign-in took too long or was already used. Please try again.",
  ambiguous_account:
    "More than one account uses this email. Please contact your admin.",
};

// Shown for "unavailable" when there is no password form to send people to.
const SSO_UNAVAILABLE_WITHOUT_PASSWORD =
  "Sign in with Google is not available right now. Please try again later.";

// The line under the heading says which ways in this page offers.
const SUBTITLE_WITH_PASSWORD =
  "Use your upGrad email and password, or your upGrad Google account.";
const SUBTITLE_GOOGLE_ONLY = "Use your upGrad Google account to sign in.";
const HELP_TEXT = "Trouble signing in? Contact your Phoenix admin.";

// Email and password login is on unless this environment's settings say false.
// A missing switch counts as on, so a settings file can never lock people out by omission.
const isPasswordLoginEnabled = () =>
  !(constants.switches && constants.switches.enablePasswordLogin === false);

const readSsoReturn = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    ssoCode: params.get(SSO_CODE_PARAM),
    ssoError: params.get(SSO_ERROR_PARAM),
  };
};

const getSsoErrorMessage = (key) => {
  if (key === "unavailable" && !isPasswordLoginEnabled()) {
    return SSO_UNAVAILABLE_WITHOUT_PASSWORD;
  }
  return Object.prototype.hasOwnProperty.call(SSO_ERROR_MESSAGES, key)
    ? SSO_ERROR_MESSAGES[key]
    : SSO_DEFAULT_ERROR;
};

const getSsoExchangeErrorMessage = (error) => {
  const message = _.get(error, "response.data.errorList[0].errorMessage");
  return typeof message === "string" && message ? message : SSO_DEFAULT_ERROR;
};

class LoginPage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      email: "",
      password: "",
      isLoading: false,
      // { verifier, url } once the browser has made its secret for a Google sign-in
      ssoStart: null,
    };
  }

  componentDidMount() {
    const { userInfo, navigationActions } = this.props;
    if (userInfo.isLoggedIn) {
      const { ssoCode, ssoError } = readSsoReturn();
      if (ssoCode || ssoError) {
        // Nothing to finish for someone already logged in. Replacing keeps the
        // address that carried the code out of the Back history.
        takeSsoVerifier();
        navigationActions.replace(routePaths.offlinePayments__overview);
        return;
      }
      navigationActions.redirect(routePaths.offlinePayments__overview);
      return;
    }
    this.handleSsoReturn();
    this.prepareSsoStart();
  }

  componentWillUnmount() {
    this.isUnmounted = true;
  }

  // The Google button is offered only once this browser holds a secret for it.
  prepareSsoStart = () => {
    createSsoVerifier().then((made) => {
      if (!made || this.isUnmounted) return;
      this.setState({
        ssoStart: {
          verifier: made.verifier,
          url: getSsoStartUrl(made.challenge),
        },
      });
    });
  };

  // Runs as the browser leaves for Google, in this tab or a new one.
  rememberSsoStart = () => {
    rememberSsoVerifier(this.state.ssoStart.verifier);
  };

  // iamauth sends the browser back here after a Google sign-in, carrying either
  // a one-time login code or an error key.
  handleSsoReturn = () => {
    const { ssoCode, ssoError } = readSsoReturn();
    if (!ssoCode && !ssoError) return;

    const { navigationActions, commonActions, userActions } = this.props;
    // The secret is good for one return, whatever that return carries.
    const verifier = takeSsoVerifier();
    // The code works once, so it leaves the address bar before anything else.
    navigationActions.replace(routePaths.loginPage);

    if (ssoError) {
      commonActions.setBackendErrorMessage(getSsoErrorMessage(ssoError));
      return;
    }
    // A code this browser did not ask for is not sent on; iamauth would refuse it.
    if (!verifier) {
      commonActions.setBackendErrorMessage(SSO_DEFAULT_ERROR);
      return;
    }

    commonActions.setBackendErrorMessage("");
    this.setState({ isLoading: true });
    userActions.loginWithSsoCode(
      ssoCode,
      verifier,
      () => {
        this.setState({ isLoading: false }, () => {
          navigationActions.redirect(routePaths.offlinePayments__overview);
        });
      },
      (error) => {
        this.setState({ isLoading: false });
        commonActions.setBackendErrorMessage(getSsoExchangeErrorMessage(error));
      }
    );
  };

  handleInputChange = ({ name, value }) => {
    this.setState({
      [name]: value,
    });
  };

  shouldSubmitDisable = () => {
    return _.isEmpty(this.state.email) || _.isEmpty(this.state.password);
  };

  handleKeyPressed = (e) => {
    if (e.key === "Enter" && !this.shouldSubmitDisable()) {
      this.handleSubmitClick();
    }
  };

  handleSubmitClick = () => {
    if (!isPasswordLoginEnabled()) return;
    const loginUser = this.props.userActions.loginUser;
    const { email, password } = this.state;
    this.props.commonActions.setBackendErrorMessage("");
    this.setState({ isLoading: true });
    loginUser(
      { email, password },
      () => {
        this.setState({ isLoading: false }, () => {
          this.props.navigationActions.redirect(
            routePaths.offlinePayments__overview
          );
        });
      },
      (error) => {
        if (this.props.userInfo.isLoggedIn) {
          this.props.userActions.logoutUser();
        }
        this.setState({ isLoading: false });
        const responseData = error.response.data;
        let errMsg = responseData.errorList
          ? responseData.errorList[0].errorMessage
          : responseData.message
          ? responseData.message.errorMessage
          : responseData;
        if (errMsg.name) errMsg = errMsg.message;
        if (typeof errMsg !== "string")
          errMsg = "Could not login into Phoenix.";

        if (errMsg && errMsg.indexOf("Invalid Token") !== -1) {
          errMsg = "You got logged out, please login again";
        }
        errMsg && this.props.commonActions.setBackendErrorMessage(errMsg);
        //In case of inactive tokens central login api is returning 200.
        // Akhil is fixing that after that this can be removed
      }
    );
  };

  render() {
    const { email, password, isLoading, ssoStart } = this.state;
    const passwordLogin = isPasswordLoginEnabled();
    return (
      <>
        <div className={styles.wrapper}>
          <img
            className={styles.leftPic}
            src="https://gw.alipayobjects.com/mdn/rms_dea682/afts/img/A*rk9bT41XC8cAAAAAAAAAAABkARQnAQ"
            alt=""
            aria-hidden="true"
          />
          <form
            autoComplete="off"
            className={styles.box}
            onSubmit={this.handleSubmitClick}
            aria-labelledby="phoenix-login-title"
          >
            <div className={styles.brand}>
              <PrimaryLogo variant="dark" />
            </div>
            <h1 id="phoenix-login-title" className={styles.header}>
              Welcome back to Phoenix 👋
            </h1>
            <p className={styles.subtitle}>
              {passwordLogin ? SUBTITLE_WITH_PASSWORD : SUBTITLE_GOOGLE_ONLY}
            </p>
            {this.props.common.backendErrorMessage && (
              <div className={styles.error} role="alert">
                {this.props.common.backendErrorMessage}
              </div>
            )}
            {passwordLogin && (
              <>
                <div className={styles.content}>
                  <Input
                    size="large"
                    label="Email"
                    name="email"
                    placeholder="name@upgrad.com"
                    className={styles.userType}
                    value={email}
                    onPressEnter={this.handleKeyPressed}
                    onChange={this.handleInputChange}
                  />
                  <Input
                    size="large"
                    type="password"
                    label="Password"
                    name="password"
                    placeholder="your password"
                    className={styles.userType}
                    value={password}
                    onPressEnter={this.handleKeyPressed}
                    onChange={this.handleInputChange}
                  />
                </div>
                <div className={styles.footer}>
                  <Button
                    size="large"
                    className={styles.submitBtn}
                    type="primary"
                    label="Log in"
                    onClick={this.handleSubmitClick}
                    loading={isLoading}
                  />
                </div>
              </>
            )}
            {ssoStart && (
              <div className={styles.sso}>
                {passwordLogin && <div className={styles.ssoDivider}>or</div>}
                <a
                  className={styles.ssoBtn}
                  href={ssoStart.url}
                  onClick={this.rememberSsoStart}
                  onAuxClick={this.rememberSsoStart}
                  onContextMenu={this.rememberSsoStart}
                >
                  <GoogleIcon
                    className={styles.ssoIcon}
                    aria-hidden="true"
                    focusable="false"
                  />
                  <span>Sign in with Google</span>
                </a>
              </div>
            )}
            <p className={styles.help}>{HELP_TEXT}</p>
          </form>
        </div>
      </>
    );
  }
}

LoginPage.propTypes = {
  userInfo: PropTypes.object.isRequired,
  userActions: PropTypes.object.isRequired,
  navigationActions: PropTypes.object.isRequired,
  commonActions: PropTypes.object.isRequired,
  common: PropTypes.object,
};
function mapStateToProps(state) {
  return {
    userInfo: state.userInfo,
    common: state.common,
  };
}
function mapDispatchToProps(dispatch) {
  return {
    userActions: bindActionCreators(userActions, dispatch),
    navigationActions: bindActionCreators(navigationActions, dispatch),
    commonActions: bindActionCreators(commonActions, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(LoginPage);
